import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { sectionRenderer } from '@theme/section-renderer';

class CartDrawerComponent extends Component {
  #summaryThreshold = 0.5;

  get #themeDrawer() {
    return this.closest('theme-drawer');
  }

  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    document.addEventListener('cart:update', this.#handleExternalCartUpdate);
    document.addEventListener('cart:refresh', this.#handleExternalCartUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    document.removeEventListener('cart:update', this.#handleExternalCartUpdate);
    document.removeEventListener('cart:refresh', this.#handleExternalCartUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
  }

  #getCartItemsComponent() {
    return this.querySelector('cart-items-component') || document.querySelector('cart-items-component[data-drawer]');
  }

  #refreshDrawerSection = async () => {
    const cartItems = this.#getCartItemsComponent();
    const sectionId = cartItems?.dataset?.sectionId;
    if (!sectionId) return;

    await sectionRenderer.renderSection(sectionId, {
      cache: false,
      mode: 'hydration',
      injectStylesheet: true,
    });
  };

  #handleExternalCartUpdate = async () => {
    try {
      await this.#refreshDrawerSection();
      if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
      requestAnimationFrame(() => this.#updateStickyState());
    } catch (error) {
      console.warn('[cart-drawer] Unable to refresh drawer section:', error);
    }
  };

  #handleDrawerOpen = () => {
    this.#updateStickyState();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;
    const sourceModal = event.target instanceof Element ? event.target.closest('dialog:modal') : null;

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = async () => {
          if (!detail?.sections) {
            await this.#refreshDrawerSection();
          }
          requestAnimationFrame(() => this.#updateStickyState());
        };

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = async () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          await settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
